import { ReactNode, useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DocumentProvider, useDocument } from '../../documents/DocumentContext';
import {
    savePolicyDocument,
    completePolicyDocument,
    createDepartmentPolicy,
    createFromTemplate,
    PopulatedPolicy,
    PolicyDocument,
    UpdatePolicyInput,
} from '../../../services/api/policyService';
import {
    stripConditionalsFromDocumentData,
    toPersistedPolicyConditionals,
} from '../conditionals/conditionalsUtils';
import { DocumentStatus } from '../../documents/types';
import { PolicyMetadata } from './PolicyMetadataBar';
import { SaveCommentDialog } from './SaveCommentDialog';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '../../ui/alert-dialog';

type SaveDocumentFn = (
    policyId: string,
    data: Record<string, any>,
    title?: string,
    comment?: string,
    policy?: UpdatePolicyInput
) => Promise<PolicyDocument>;

interface PolicyProviderProps {
    children: ReactNode;
    initialData?: Record<string, any>;
    policyId?: string;
    documentData?: { data?: Record<string, any>; status?: DocumentStatus };
    onDataChange?: (data: Record<string, any>) => void;
    getMeta: () => PolicyMetadata;
    schemaKey?: string | null;
    templateId?: string | null;
    onCreated?: (policy: PopulatedPolicy) => void;
    onCompleted?: () => void;
    onSavedAndClose?: () => void;
}

interface PendingSave {
    data: Record<string, any>;
    saveFn: SaveDocumentFn;
    isComplete: boolean;
    afterSave: 'continue' | 'close';
    status: DocumentStatus;
    resolve: (result: { documentId?: string; status?: DocumentStatus }) => void;
    reject: (error: unknown) => void;
}

export function PolicyProvider({
    children,
    initialData = {},
    policyId: initialPolicyId,
    documentData,
    onDataChange,
    getMeta,
    schemaKey,
    templateId,
    onCreated,
    onCompleted,
    onSavedAndClose,
}: PolicyProviderProps) {
    const { t } = useTranslation();
    const policyIdRef = useRef(initialPolicyId);
    const createdRef = useRef(false);
    const [pendingSave, setPendingSave] = useState<PendingSave | null>(null);
    const [missingDatesError, setMissingDatesError] = useState(false);
    const [expiryError, setExpiryError] = useState(false);

    const createPolicy = async (): Promise<string | null> => {
        if (createdRef.current) return null;

        const newPolicy = templateId
            ? await createFromTemplate(templateId)
            : schemaKey
            ? await createDepartmentPolicy({ schemaKey })
            : (() => {
                  throw new Error('No schema or template specified');
              })();

        createdRef.current = true;
        policyIdRef.current = newPolicy._id;
        onCreated?.(newPolicy);

        return newPolicy._id;
    };

    const executeSave = async (
        data: Record<string, any>,
        saveFn: SaveDocumentFn,
        status: DocumentStatus,
        comment?: string
    ): Promise<{ documentId?: string; status?: DocumentStatus }> => {
        const meta = getMeta();
        const policyId = policyIdRef.current ?? (await createPolicy());
        if (!policyId) return {};

        const persistedConditionals = toPersistedPolicyConditionals(
            data.policyConditionals
        );
        const documentPayload = stripConditionalsFromDocumentData(data);

        const result = await saveFn(
            policyId,
            documentPayload,
            meta.title || undefined,
            comment,
            {
                effectiveDate: meta.effectiveDate,
                expiryDate: meta.expiryDate,
                tags: meta.tags,
                coAuthors: meta.coAuthors,
                attachments: meta.attachments,
                conditionals: persistedConditionals,
                status,
                requireApproval: meta.requireApproval,
            }
        );

        return { documentId: result._id, status: result.status };
    };

    const requestSave = useCallback(
        (
            data: Record<string, any>,
            saveFn: SaveDocumentFn,
            isComplete: boolean,
            afterSave: 'continue' | 'close' = 'continue',
            status: DocumentStatus = 'draft'
        ) =>
            new Promise<{ documentId?: string; status?: DocumentStatus }>((resolve, reject) => {
                setPendingSave({
                    data,
                    saveFn,
                    isComplete,
                    afterSave,
                    status,
                    resolve,
                    reject,
                });
            }),
        []
    );

    const handleConfirmSave = useCallback(
        async (mode: 'complete' | 'continue' | 'close', comment?: string) => {
            if (!pendingSave) return;
            const { data, saveFn, isComplete, afterSave, status, resolve, reject } =
                pendingSave;
            setPendingSave(null);
            try {
                const result = await executeSave(data, saveFn, status, comment);
                if (isComplete || mode === 'complete') {
                    onCompleted?.();
                } else if (afterSave === 'close' || mode === 'close') {
                    onSavedAndClose?.();
                }
                resolve(result);
            } catch (err) {
                reject(err);
            }
        },
        [onCompleted, onSavedAndClose, pendingSave]
    );

    const handleCancelSave = useCallback(() => {
        if (!pendingSave) return;
        pendingSave.resolve({});
        setPendingSave(null);
    }, [pendingSave]);

    const hasRequiredDates = useCallback(() => {
        const meta = getMeta();
        return !!meta.effectiveDate && !!meta.expiryDate;
    }, [getMeta]);

    return (
        <DocumentProvider
            initialData={initialData}
            documentId={policyIdRef.current}
            documentData={documentData}
            onDataChange={onDataChange}
            saveDocument={async (data) => {
                if (!hasRequiredDates()) {
                    setMissingDatesError(true);
                    return {};
                }

                return requestSave(data, savePolicyDocument, false, 'continue', 'draft');
            }}
            completeDocument={async (data) => {
                if (!hasRequiredDates()) {
                    setMissingDatesError(true);
                    return {};
                }

                const meta = getMeta();
                if (meta.expiryDate) {
                    const expiry = new Date(meta.expiryDate);
                    const today = new Date();
                    today.setHours(0, 0, 0, 0);
                    if (expiry < today) {
                        setExpiryError(true);
                        return {};
                    }
                }
                const alreadyCompleted = documentData?.status === 'completed';
                return requestSave(
                    data,
                    alreadyCompleted ? savePolicyDocument : completePolicyDocument,
                    true,
                    'continue',
                    'completed'
                );
            }}
        >
            {children}
            <SaveCommentDialog
                open={!!pendingSave}
                isComplete={pendingSave?.isComplete ?? false}
                onConfirm={handleConfirmSave}
                onCancel={handleCancelSave}
            />
            <AlertDialog
                open={missingDatesError}
                onOpenChange={setMissingDatesError}
            >
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>
                            {t('departmentPolicies.validation.datesRequiredTitle')}
                        </AlertDialogTitle>
                        <AlertDialogDescription>
                            {t('departmentPolicies.validation.datesRequiredDescription')}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogAction>{t('departmentPolicies.validation.ok')}</AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
            <AlertDialog open={expiryError} onOpenChange={setExpiryError}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>{t('departmentPolicies.validation.expiryPassedTitle')}</AlertDialogTitle>
                        <AlertDialogDescription>
                            {t('departmentPolicies.validation.expiryPassedDescription')}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogAction>{t('departmentPolicies.validation.ok')}</AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </DocumentProvider>
    );
}

export const usePolicy = () => {
    const context = useDocument();
    return {
        data: context.data,
        updateData: context.updateData,
        savePolicy: context.save,
        completePolicy: context.complete,
        isSaving: context.isSaving,
        isLoading: context.isLoading,
        policyDocumentId: context.documentId,
        policyStatus: context.documentStatus,
        setValue: context.setValue,
    };
};
